import { Component, HostListener, OnInit, ElementRef, ViewContainerRef, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OAuthEmitterService, OAuthEvent } from '@nationwide/angular-oauth-module';
import { environment } from '../environments/environment';
import { TimeoutModalService } from './shared/session/time-out/time-out.service';
import { isExternal } from './shared/mixins/session-clearing.component';
import { ConsumerConfigurerService } from './billing-timeline-widget/service/consumer-configurer.service';
import { URL_CONSTANT, MILLISECONDS_IN_SECOND } from './shared/constants/common.constants';
import { PersonalBillingConfiguration } from '@nationwide/dgs-angular-billing-common';
import { SessionService } from './shared/session/session.service';
import { PaymentFlowType } from './pay-bill/shared/payment-flow-type.service';
import { UrlUtil } from './shared/url-util/url-util.service';
import { LoggerService } from './shared/logger/logger.service';
import { BillingEventService } from './shared/service/ebi-service/billing-events.service';
import { EBIEventType } from './shared/service/ebi-service/ebi-model';
import { FooterService } from './shared/footer/footer.service';
import { FooterData } from '@nationwide/internet-servicing-angular-components';
import { HttpCachingService } from './shared/service/http-caching-service/http-caching.service';
import { Subscription } from 'rxjs';
import { OauthClientService } from './shared/oauth-client.service';
import { ClickToChatService } from './shared/service/click-to-chat/click-to-chat.service';
import { CelebrusTrackingService } from './shared/service/celebrus-tracking/celebrus-tracking.service';
import { EUAv3IframeService } from './shared/service/euav3-iframe/euav3-iframe.service';
import { PingRedirectService } from './shared/service/ping-redirect/ping-redirect.service';

@Component({
    selector: 'app-root',
    standalone: false,
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})

export class AppComponent implements OnInit {
    // TODO canLoadApplication = false currently creates error in console:
    // core.js:4127 ERROR Error: ExpressionChangedAfterItHasBeenCheckedError:
    // Expression has changed after it was checked. Find work around so value
    // does not change from false to true before page is loaded.
    canLoadApplication = signal(false);
    shouldHideWaitMessage = signal(false);
    isLoadedByExternalApplication = false;
    skipOAuth = false;
    footer = signal<FooterData | undefined>(undefined);
    isLoadedByBAM = signal(false);
    pingRenewalListener?: Subscription;
    euaListener?: Subscription;
    renewTokenListener?: Subscription;

    constructor(
        private router: Router,
        private readonly route: ActivatedRoute,
        private timeoutModalService: TimeoutModalService,
        private oauthEmitterService: OAuthEmitterService,
        private elementRef: ElementRef,
        private session: SessionService,
        private consumerConfigurerService: ConsumerConfigurerService,
        private personalBillingConfiguration: PersonalBillingConfiguration,
        private paymentFlowType: PaymentFlowType,
        private urlUtil: UrlUtil,
        private readonly eventService: BillingEventService,
        private logger: LoggerService,
        private footerService: FooterService,
        private httpCachingService: HttpCachingService,
        private activatedRoute: ActivatedRoute,
        private readonly oauthClientService: OauthClientService,
        private readonly container: ViewContainerRef,
        private clickToChatService: ClickToChatService,
        private celebrusTrackingService: CelebrusTrackingService,
        private euav3IframeService: EUAv3IframeService,
        private pingRedirectService: PingRedirectService
    ) {
        this.clickToChatService.checkToPreserveChatOnLoad();
    }

    @HostListener('window:mousedown', ['$event'])
    @HostListener('window:mouseup', ['$event'])
    @HostListener('window:scroll', ['$event'])
    onSessionActivity(): void {
        this.timeoutModalService.recordSessionActivity();
    }

    @HostListener('window:click', ['$event'])
    handleClick(event: Event): void {
        const target = event && <HTMLElement> event.target;
        if (this.isExternalNavClick(target)) {
            const externalUrl: string = this.getExternalUrl(target);
            this.session.externalAppPageName = this.urlUtil.extractPageUrl(externalUrl);
            this.session.logOutUser();
        }
    }

    @HostListener('window:beforeunload', ['$event'])
    leavingApp(): void {
        this.logger.info('Leaving ISB');
        this.logger.logCurrentContents();
        sessionStorage.setItem('browserRefresh', 'true');
        this.session.clearAccessToken();
        this.session.clearPageInfo();
        this.session.clearFlowIndicator();
    }

    @HostListener('window:popstate', ['$event'])
    onPopState(): void {
        const goingTo = window.location.href;
        const comingFromBkey = this.route.snapshot.queryParams[environment.QUERY_PARAMS.billingAccount] || 'noBkey';
        this.navigateToNewBillingAccountCheck(goingTo, comingFromBkey);
    }

    ngOnInit(): void {
        this.logger.generateUniqueId();
        this.logger.info('App Initializing...');
        this.setBAMApplicationFlag();
        this.fetchFooter();
        if (this.pingRedirectService.isBackFromRedirect) {
            this.logger.info('isBackFromRedirect is true');
            this.pingRedirectService.handleBackFromRedirect();
        }
        this.setExternalApplicationFlag();
        this.loadNRScript();
        this.setQueryParamsToSession();
        if (this.isLoadedByExternalApplication) {
            this.consumerConfigurerService.setupTimeline(this.elementRef);
            if (this.isOauthRequired) {
                this.oauthEmitterService.oauthEmitter.subscribe((event: OAuthEvent) => {
                    if (!this.oauthEmitterService.inDescendantIframe()) {
                        this.reactTo(event);
                    }
                });
            } else {
                this.setCanLoadApplication(true);
            }
            this.router.navigateByUrl(URL_CONSTANT.BILLING_TIMELINE_ROUTE_URL);
        } else if (this.shouldSkipOAuth()) {
            this.skipOAuth = true;
            this.setCanLoadApplication(true);
        } else if (this.session.isBrowserBackAfterLogOut) {
            this.logger.info('Leaving ISB isBrowserBackAfterLogOut is true');
            this.session.gotoLogin();
        } else {
            const oAuthtoken = this.getUrlParameter('oauth_token');
            const mpStatus = this.getUrlParameter('mpstatus');
            if (oAuthtoken && mpStatus === 'success') {
                this.logger.info(`oAuthtoken exisits and mpStatus is successful`);
                sessionStorage.setItem('oAuthToken', oAuthtoken);
                const paymentUrl = `${window.location.origin}/${window.location.hash}`;
                window.location.replace(paymentUrl);
            } else {
                this.initEUA3Auth();
            }
        }
        this.logger.logCurrentContents();
    }

    private initEUA3Auth(): void {
        // Browser refresh shortcut: skip re-auth if valid tokens already exist in session
        const isBrowserRefresh = sessionStorage.getItem('browserRefresh') === 'true';
        const hasAccessToken = !!sessionStorage.getItem('access_token');
        const hasValidTokenDetails = this.hasValidTokenDetails();

        if (isBrowserRefresh && hasAccessToken && hasValidTokenDetails) {
            this.logger.info('Browser refresh detected with valid tokens — skipping re-auth');
            sessionStorage.removeItem('browserRefresh');
            this.setupTokenRenewal();
            this.reactTo({ successful: true });
            return;
        }

        if (isBrowserRefresh) {
            sessionStorage.removeItem('browserRefresh');
        }

        // Standard EUA3 iframe PKCE auth
        this.logger.info('Initiating EUA3 iframe PKCE authentication');
        this.euav3IframeService.initiateIframeAuth().subscribe({
            next: (result) => {
                if (result.successful) {
                    this.logger.info('EUA3 auth completed successfully');
                    this.setupTokenRenewal();
                    this.reactTo({ successful: true });
                } else {
                    this.logger.error('EUA3 auth returned unsuccessful result', { error: result.error });
                    this.router.navigateByUrl('/personal/error/system-down');
                }
            },
            error: (error) => {
                this.logger.error('EUA3 iframe auth failed', error);
                this.router.navigateByUrl('/personal/error/system-down');
            }
        });
    }

    private setupTokenRenewal(): void {
        if (!this.renewTokenListener || this.renewTokenListener.closed) {
            this.renewTokenListener = this.timeoutModalService.renewToken$.subscribe((shouldRenewToken: boolean) => {
                if (shouldRenewToken) {
                    this.timeoutModalService.isRenewing = true;
                    this.logger.info('Renewing EUA3 token via refresh_token grant');
                    const refreshToken = sessionStorage.getItem('refresh_token');
                    if (refreshToken) {
                        this.euav3IframeService.refreshToken().subscribe({
                            next: () => {
                                this.timeoutModalService.isRenewing = false;
                                this.logger.info('EUA3 token renewal successful');
                            },
                            error: (error) => {
                                this.timeoutModalService.isRenewing = false;
                                this.logger.error('EUA3 token renewal failed', error);
                                if (this.isCriticalTokenRefreshError(error)) {
                                    this.session.logOutUser();
                                }
                            }
                        });
                    } else {
                        this.logger.warn('No refresh_token available — re-initiating iframe auth');
                        this.timeoutModalService.isRenewing = false;
                        this.initEUA3Auth();
                    }
                }
            });
        }
    }

    triggerEbiEvent(): void {
        return this.eventService.handleEvent(EBIEventType.ON_CLICK_OF_CONTACT_US_LINK_LOGS_OUT_OF_ISB);
    }

    setCanLoadApplication(value: boolean): void {
        this.logger.info(`Setting canLoadApplication to ${value}`);
        this.canLoadApplication.set(value);
        this.clickToChatService.initializeChat();
        if (!this.shouldHideWaitMessage() && value) {
            setTimeout(() => {
                this.shouldHideWaitMessage.set(true);
            }, MILLISECONDS_IN_SECOND * 2);
        }
    }

    shouldSkipOAuth(): boolean {
        let shouldSkip = false;
        const isQuickPayBackFromRedirect = this.pingRedirectService.isBackFromRedirect &&
            this.session.pingRedirectInfo?.callingRoute === environment.ISB.ENDPOINTS.QUICK_PAY.quickPayLoginRedirect;

        if (this.paymentFlowType.isQuickPay || isQuickPayBackFromRedirect ||
            this.urlUtil.hashPath === '#/personal/billing/landing' || this.urlUtil.hashPath === '#/personal/stub/billing-stub') {
            shouldSkip = true;
        }

        return shouldSkip;
    }
    getExternalUrl(eventTarget: HTMLElement): string {
        return eventTarget && (<HTMLLinkElement> eventTarget).href;
    }

    navigateToNewBillingAccountCheck(goingTo: string, comingFrom: string): void {
        if (!goingTo.includes(comingFrom)) {
            this.refreshPage();
        }
    }

    refreshPage(): void {
        location.reload();
    }

    private loadNRScript(): void {
        // const script = document.createElement('script');
        // script.type = 'text/javascript';
        // script.src = environment.BAMNR;
        // document.getElementsByTagName('head')[0].appendChild(script);
    }

    private getUrlParameter(name: string): string {
        name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');

        const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
        const results = regex.exec(window.location.search);
        return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
    }

    private reactTo(event: OAuthEvent | { successful: boolean }): void {
        if ((event as { successful?: boolean }).successful) {
            this.timeoutModalService.pollTimeOut(this.container);
            this.httpCachingService.checkAndRetrieveContextCache().subscribe({
                next: (contextCacheResponse) => {
                    if (contextCacheResponse?.context?.accountNumber) {
                        this.logger.info('Context cache retrieved successfully.');
                        this.session.setIsLifeBillingNavigationFlow('true');
                        const contextCacheAgreementNumber = contextCacheResponse.context.accountNumber.toString();
                        this.session.setBillingAccountNumberToSession(contextCacheAgreementNumber);
                        this.httpCachingService.fetchRetrieveCustomerAgreementResponse().subscribe((response) => {
                            const billingAccount = contextCacheAgreementNumber;
                            const sessionId = response.retrieveCustomerAgreementResponse?.return?.profile?.sessionId;
                            this.setA2ASessionIdAndBKey(billingAccount, sessionId);
                            // Initialize Celebrus tracking after session data is loaded
                            this.celebrusTrackingService.initializeCelebrusSessionStorage();
                        });
                        this.setCanLoadApplication(true);
                        this.timeoutModalService.pollTimeOut(this.container);
                    } else {
                        this.setCanLoadApplication(true);
                        this.timeoutModalService.pollTimeOut(this.container);
                        // Initialize Celebrus tracking even if context cache is empty
                        this.celebrusTrackingService.initializeCelebrusSessionStorage();
                    }
                },
                error: (error) => {
                    this.logger.error('Error retrieving context cache.', error);
                    this.setCanLoadApplication(true);
                }
            });
        } else if (this.router.url.includes('/pay-bill/review')) {
            this.setCanLoadApplication(true);
        } else {
            this.setCanLoadApplication(true);
            this.router.navigateByUrl('/personal/error/system-down');
        }
    }

    private setA2ASessionIdAndBKey(billingAccount: string, sessionId: string): void {
        const params = { ...this.activatedRoute.snapshot.queryParams };
        if (!params.bkey) {
            params['bkey'] = billingAccount;
        }
        if (!params.myAccountUserSessionId) {
            params['myAccountUserSessionId'] = sessionId;
        }
        this.router.navigate([], { queryParams: params });
    }

    private isExternalNavClick(eventTarget: HTMLElement): boolean {
        if ((<HTMLLinkElement> eventTarget).href === environment.CONTACT_US_URL) {
            this.triggerEbiEvent();
        }
        return !!eventTarget &&
            !!eventTarget.getAttribute && (

                eventTarget.getAttribute('external') !== null ||

            isExternal((<HTMLLinkElement> eventTarget).href) && eventTarget.getAttribute('target') !== '_blank'

            );
    }

    private setExternalApplicationFlag(): void {
        const inputValue: string = this.elementRef.nativeElement.getAttribute('is-external-application');
        this.isLoadedByExternalApplication = inputValue === 'true';
    }

    private setBAMApplicationFlag(): void {
        this.isLoadedByBAM.set(window.location.href.includes('BAM/BillingTimeline'));
    }

    get isOauthRequired(): boolean {
        return !!this.personalBillingConfiguration.isAccessTokenRequired;
    }

    private fetchFooter(): void {
        this.logger.info('Initializing footer');
        this.footer.set(this.footerService.defaultFooter(!this.paymentFlowType.isQuickPay));
        this
            .footerService
            .fetchFooter(!this.paymentFlowType.isQuickPay)
            .subscribe((footer) => {
                this.logger.info('Initializing footer complete');
                this.footer.set(footer);
            });
    }

    private setQueryParamsToSession(): void {
        const loginTokenParam = environment.OAUTH_CONFIG.loginTokenParam;
        const loginTokenStorageKey = environment.OAUTH_CONFIG.loginTokenSessionStorageLocation;
        if (!loginTokenParam || !loginTokenStorageKey) {
            return;
        }

        const hashParams = this.urlUtil.hashParams as Record<string, string | undefined>;
        const loginToken = hashParams[loginTokenParam];

        if (!environment.PRODUCTION && loginToken) {
            sessionStorage.setItem(loginTokenStorageKey, loginToken);
        }
    }

    private isCriticalTokenRefreshError(error: { status?: number } | undefined): boolean {
        return error?.status === 401 || error?.status === 403;
    }

    private hasValidTokenDetails(): boolean {
        const tokenDetails = sessionStorage.getItem('tokenDetails');

        if (!tokenDetails) {
            return false;
        }

        const details = tokenDetails.split(',').map((part) => part.trim());
        if (details.length < 2) {
            return false;
        }

        const expiryTime = Number(details[1]);
        return Number.isFinite(expiryTime) && expiryTime > Date.now();
    }
}
